/**
 * certificateFields — which inputs a certificate type has, and what they are called.
 *
 * Both maps are lifted verbatim from the legacy presenter
 * (`source/js/presenters/users-certificates.js`, `$scope.fields` and
 * `$scope.fieldsName`), where they are FRONTEND constants — not server data.
 * That is worth recording: the API gap audit assumed a certificate "field
 * visibility map" would have to come from the backend, and it does not. What
 * the server supplies is the catalogs (types, grades, ratings), which the page
 * already receives through `{% block JSON %}`.
 *
 * `FIELDS[typeId]`      → booleans deciding which controls exist, plus
 *                         `instrumentalID`, the rating type treated as the
 *                         instrument rating for that certificate.
 * `FIELD_LABELS[typeId]` → the wording for those controls; the same input is
 *                         "Grade" on a pilot certificate and "Class" on a
 *                         medical, "Date Of Issue" vs "Date of Examination".
 */
export const FIELDS = {
	1: {
		grade: true,
		certificateNumber: true,
		dateOfIssue: true,
		ratingsWithPrivilege: true,
		ratingsWithoutPrivilege: false,
		ratingsWithoutType: false,
		ratingsNonUs: false,
		ratingsInput: false,
		instrumental: true,
		instrumentalID: 6,
		limitations: true,
		dateExpires: false,
		dateExpiresMedical: false,
		specialIssuance: false,
		fileNumber: false,
		frn: false,
		gradeNonUs: false
	},
	2: {
		grade: false,
		certificateNumber: true,
		dateOfIssue: true,
		ratingsWithPrivilege: false,
		ratingsWithoutPrivilege: true,
		ratingsWithoutType: false,
		ratingsNonUs: false,
		ratingsInput: false,
		instrumental: true,
		instrumentalID: 6,
		limitations: true,
		dateExpires: true,
		dateExpiresMedical: false,
		specialIssuance: false,
		fileNumber: false,
		frn: false,
		gradeNonUs: false
	},
	3: {
		grade: false,
		certificateNumber: true,
		dateOfIssue: true,
		ratingsWithPrivilege: false,
		ratingsWithoutPrivilege: false,
		ratingsWithoutType: true,
		ratingsNonUs: false,
		ratingsInput: false,
		instrumental: false,
		instrumentalID: 6,
		limitations: true,
		dateExpires: false,
		dateExpiresMedical: false,
		specialIssuance: false,
		fileNumber: false,
		frn: false,
		gradeNonUs: false
	},
	4: {
		grade: true,
		certificateNumber: false,
		dateOfIssue: true,
		ratingsWithPrivilege: false,
		ratingsWithoutPrivilege: false,
		ratingsWithoutType: false,
		ratingsNonUs: false,
		ratingsInput: false,
		instrumental: false,
		instrumentalID: 6,
		limitations: true,
		dateExpires: true,
		dateExpiresMedical: true,
		specialIssuance: true,
		fileNumber: false,
		frn: false,
		gradeNonUs: false
	},
	5: {
		grade: false,
		certificateNumber: true,
		dateOfIssue: true,
		ratingsWithPrivilege: false,
		ratingsWithoutPrivilege: false,
		ratingsWithoutType: false,
		ratingsNonUs: false,
		ratingsInput: false,
		instrumental: false,
		instrumentalID: 6,
		limitations: true,
		dateExpires: false,
		dateExpiresMedical: false,
		specialIssuance: false,
		fileNumber: false,
		frn: false,
		gradeNonUs: false
	},
	6: {
		grade: false,
		certificateNumber: true,
		dateOfIssue: true,
		ratingsWithPrivilege: false,
		ratingsWithoutPrivilege: false,
		ratingsWithoutType: false,
		ratingsNonUs: false,
		ratingsInput: false,
		instrumental: false,
		instrumentalID: 6,
		limitations: true,
		dateExpires: false,
		dateExpiresMedical: false,
		specialIssuance: false,
		fileNumber: true,
		frn: true,
		gradeNonUs: false
	},
	7: {
		grade: false,
		certificateNumber: true,
		dateOfIssue: true,
		ratingsWithPrivilege: false,
		ratingsWithoutPrivilege: false,
		ratingsWithoutType: true,
		ratingsNonUs: false,
		ratingsInput: false,
		instrumental: false,
		instrumentalID: 6,
		limitations: true,
		dateExpires: false,
		dateExpiresMedical: false,
		specialIssuance: false,
		fileNumber: false,
		frn: false,
		gradeNonUs: false
	},
	8: {
		grade: false,
		certificateNumber: true,
		dateOfIssue: true,
		ratingsWithPrivilege: false,
		ratingsWithoutPrivilege: false,
		ratingsWithoutType: false,
		ratingsNonUs: true,
		ratingsInput: true,
		instrumental: false,
		instrumentalID: 6,
		limitations: true,
		dateExpires: false,
		dateExpiresMedical: false,
		specialIssuance: false,
		fileNumber: false,
		frn: false,
		gradeNonUs: true
	}
};

export const FIELD_LABELS = {
	1: { grade: 'Grade', certificateNumber: 'Certificate Number', dateOfIssue: 'Date Of Issue' },
	2: {
		grade: 'Grade',
		certificateNumber: 'Certificate Number',
		dateOfIssue: 'Date Of Issue',
		dateExpires: 'Expires'
	},
	3: { grade: 'Grade', certificateNumber: 'Certificate Number', dateOfIssue: 'Date Of Issue' },
	4: {
		grade: 'Class',
		certificateNumber: 'Certificate Number',
		dateOfIssue: 'Date of Examination',
		specialIssuance: 'Special Issuance',
		dateExpires: 'Expiration Date'
	},
	5: { grade: 'Grade', certificateNumber: 'Certificate Number', dateOfIssue: 'Date Of Issue' },
	6: {
		grade: 'Grade',
		certificateNumber: 'Serial Number',
		dateOfIssue: 'Grant Date',
		frn: 'FRN',
		frnDescription: 'FCC Registration Number',
		fileNumber: 'File Number'
	},
	7: { grade: 'Grade', certificateNumber: 'Certificate Number', dateOfIssue: 'Date Of Issue' },
	8: { grade: 'Grade', certificateNumber: 'Certificate Number', dateOfIssue: 'Date Of Issue' }
};
const EMPTY = {};

/** Field flags for a certificate type id (never null). */
export function fieldsForType(typeId) {
	if (typeId == null || typeId === '') return EMPTY;
	return FIELDS[Number(typeId)] || EMPTY;
}

/** Labels for a certificate type id (never null). */
export function labelsForType(typeId) {
	if (typeId == null || typeId === '') return EMPTY;
	return FIELD_LABELS[Number(typeId)] || EMPTY;
}

/** The label for one field, with a sensible fallback. */
export function labelFor(typeId, key, fallback) {
	return labelsForType(typeId)[key] || fallback;
}
